import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Button, CircularProgress, Grid, Modal, Typography } from '@mui/material';

import { callApi } from 'src/core/callApi';
import AutohideSnackbar from 'src/App/Dashboards/Market/Components/Alerts/AutohideSnackbar';
import NameConfirmationModal from './NameConfirmationModal';

import * as turf from '@turf/turf';
import { generateWKTPolygon } from 'src/utils/functions';
import { setTailoredRegionCreationRateLimited } from 'src/store/Dashboard/actions';

const SubmarketCreationModal = ({
  open,
  setOpen,
  setSubmarketPolygonPaths,
  setBoundarySelectionModalOpen,
  submarketCreationSource,
  parentMarketID,
  setNewlyAddedSubmarketId,
  fetchSubMarketData,
  tailoredSubmarketPolygon,
  setTailoredSubmarketPolygon,
}) => {
  const search = useSelector(state => (state as any).dashboard.search);
  const dispatch = useDispatch();
  const [submarketName, setSubmarketName] = useState('');
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleModalOnClose = () => {
    setSubmarketPolygonPaths([]);
    setTailoredSubmarketPolygon(null);
    setBoundarySelectionModalOpen(true);
    setOpen(false);
  };

  const sanitizePolygon = (
    path: google.maps.LatLngLiteral[],
    simplifyTolerance = 0.0001
  ): string | null => {
    if (!path || path.length < 3) return null;

    try {
      // Convert [{lat, lng}] → [[lng, lat]]
      let coords: number[][] = path.map(p => [p.lng, p.lat]);

      // Remove duplicate points (not just consecutive)
      const seen = new Set<string>();
      coords = coords.filter(pt => {
        const key = `${pt[0].toFixed(6)},${pt[1].toFixed(6)}`; // round to 6 decimals
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });

      // Ensure closed ring
      const first = coords[0];
      const last = coords[coords.length - 1];
      if (first[0] !== last[0] || first[1] !== last[1]) {
        coords.push(first);
      }

      if (coords.length < 4) {
        return null; // not enough unique points for a polygon
      }

      // Create Turf polygon
      let polygon = turf.polygon([coords]);

      // Clean coordinates (removes spikes, redundant vertices)
      const cleaned = turf.cleanCoords(polygon);

      // if (simplifyTolerance > 0) {
      //   polygon = turf.simplify(polygon, { tolerance: simplifyTolerance, highQuality: false });
      // }

      // Fix self-intersections (splits into simple polygons)
      const unkinked = turf.unkinkPolygon(cleaned);

      if (!unkinked || unkinked.features.length === 0) return null;

      // Pick the largest valid polygon
      const largest = unkinked.features.sort((a, b) => turf.area(b) - turf.area(a))[0];

      const finalCoords = largest.geometry.coordinates[0];

      // Use your existing function
      return generateWKTPolygon(finalCoords);
    } catch (err) {
      console.error('Polygon sanitization error:', err);
      return null;
    }
  };

  const createSubmarket = async () => {
    setLoading(true);
    setErrorMessage(null);
    setSuccess(false);

    const url = 'https://oz287au41h.execute-api.us-east-2.amazonaws.com/default/createTailorRegion';

    if (!tailoredSubmarketPolygon) return;

    let sanitizedPolygon: string | null = null;

    try {
      sanitizedPolygon = sanitizePolygon(tailoredSubmarketPolygon);

      if (!sanitizedPolygon) return;
    } catch (error) {
      console.error('Error sanitizing polygon:', error);
      return;
    }

    const requestConfig = {
      queryParams: {
        countryCode: search.data.countryCode,
        polygon_string_wkt: sanitizedPolygon,
        parentMarketID: parentMarketID ? parentMarketID : search.data.id,
        apiType: 'v2'
      },
    };

    try {
      const data = await callApi(
        url, 
        requestConfig, 
        true, 
        false, 
        '', 
        'GET', 
        true, 
        undefined, 
        true, 
        true
      );

      if (data && data.status === 429) {
        dispatch(setTailoredRegionCreationRateLimited(true));
        setLoading(false);
        setSuccess(false);
        return 'failed';
      }

      if (data.error) {
        setErrorMessage(data.error);
      } else {
        const submarketName = data.message.district;
        const submarketID = data.message.market_id;

        if (!submarketID) {
          setErrorMessage('Failed to create submarket');
          return 'failed';
        }

        setSuccess(true);
        setSubmarketName(submarketName);
        setNewlyAddedSubmarketId(submarketID);
      }
    } catch (error) {
      console.error('Error handling submarket creation:', error);
      return 'failed';
    } finally {
      setLoading(false);
    }
    return;
  };

  useEffect(() => {
    createSubmarket();
  }, []);

  const handleBoundarySelectionModalClose = async () => {
    if (submarketCreationSource === 'map') {
      setBoundarySelectionModalOpen(false);
    } else {
      const res = await createSubmarket();
      if (res !== 'failed') {
        setBoundarySelectionModalOpen(false);
      }
    }
  };

  const redirectToSubmarket = () => {
    handleModalOnClose();
    return;

    const submarketURL = `${window.location.origin}/airbnb-data/0/0/${submarketName}`;
    setTimeout(() => {
      window.open(submarketURL, '_blank');
      handleModalOnClose();
    }, 2000);
  };

  return (
    <>
      <Modal open={open} onClose={handleModalOnClose}>
        <Grid
          container
          direction="column"
          sx={{
            position: 'absolute' as 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '100%',
            height: '100%',
            outline: 'none !important',
            bgcolor: '#fff0',
            boxShadow: 24,
            borderRadius: 2,
            pt: 2,
            px: 4,
            pb: 3,
            overflowY: 'scroll',
          }}
        >
          {loading || errorMessage ? (
            <>
              <Box
                sx={{
                  p: 4,
                  display: 'flex',
                  // justifyContent: 'center',
                  alignItems: 'center',
                  height: '150px',
                  width: 'clamp(200px, 50vw, 400px)',
                  background: '#fff',
                  borderRadius: '12px',
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                }}
              >
                {loading ? (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      justifyContent: 'center',
                      width: '100%',
                    }}
                  >
                    <CircularProgress />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      gap: 1,
                      width: '100%',
                    }}
                  >
                    <Typography variant="body1">{errorMessage}</Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      sx={{ alignSelf: 'flex-end' }}
                      onClick={() => {
                        handleModalOnClose();
                      }}
                    >
                      Close
                    </Button>
                  </Box>
                )}
              </Box>
            </>
          ) : (
            <NameConfirmationModal
              open={true}
              onClose={() => {
                redirectToSubmarket();
                setOpen(false);
                fetchSubMarketData();
              }}
              onConfirm={() => {
                fetchSubMarketData();
                redirectToSubmarket();
                setOpen(false);
              }}
              submarketName={submarketName}
              setSubmarketName={setSubmarketName}
            />
          )}
        </Grid>
      </Modal>

      {errorMessage && <AutohideSnackbar message={errorMessage} type="error" />}
      {success && (
        <AutohideSnackbar
          message={
            ' Submarket created! Please ensure your browser allows pop-ups to view the new window.'
          }
          type="success"
        />
      )}
    </>
  );
};

export default SubmarketCreationModal;
